import {
  TurnOperationAbandonedError,
  type AcceptedTurn,
  type TurnAbandonReason,
  type TurnOperation,
  type TurnTerminalOutcome,
} from '../../contracts';

const RECENT_TERMINAL_MEMORY = 16;

export type NamedTurnTerminalKind = 'completed' | 'cancelled' | 'failed';

type AcceptedDeferred = {
  resolve: (value: AcceptedTurn) => void;
  reject: (error: unknown) => void;
};

type TerminalDeferred = {
  resolve: (value: TurnTerminalOutcome) => void;
};

type OperationRecord = {
  clientTurnId: string;
  acceptedTurnId: string | null;
  accepted: AcceptedDeferred;
  terminal: TerminalDeferred;
  acceptedSettled: boolean;
  terminalSettled: boolean;
};

type RememberedNamedTerminal = {
  turnId: string;
  kind: NamedTurnTerminalKind;
};

const attachRejectionObserver = (accepted: Promise<AcceptedTurn>): void => {
  void accepted.catch(() => undefined);
};

const createAcceptedPromise = (): {
  promise: Promise<AcceptedTurn>;
  deferred: AcceptedDeferred;
} => {
  const deferred: AcceptedDeferred = {
    resolve: () => undefined,
    reject: () => undefined,
  };
  const promise = new Promise<AcceptedTurn>((resolve, reject) => {
    deferred.resolve = resolve;
    deferred.reject = reject;
  });
  attachRejectionObserver(promise);
  return { promise, deferred };
};

const createTerminalPromise = (): {
  promise: Promise<TurnTerminalOutcome>;
  deferred: TerminalDeferred;
} => {
  const deferred: TerminalDeferred = {
    resolve: () => undefined,
  };
  const promise = new Promise<TurnTerminalOutcome>((resolve) => {
    deferred.resolve = resolve;
  });
  return { promise, deferred };
};

/** A refused start that never enters the registry. */
export const createRefusedTurnOperation = (
  turnId: string,
  error: unknown,
  reason: TurnAbandonReason = 'submit_rejected'
): TurnOperation => {
  const accepted = Promise.reject(
    error instanceof Error ? error : new Error('Turn submission refused')
  );
  attachRejectionObserver(accepted);
  return {
    turnId,
    accepted,
    terminal: Promise.resolve({ kind: 'abandoned', turnId, reason }),
  };
};

export type DeferredTurnOperation = {
  readonly operation: TurnOperation;
  adopt: (real: TurnOperation) => void;
  abandon: (reason: TurnAbandonReason, error?: unknown) => void;
};

/**
 * An operation that will adopt a later real start, or settle abandoned if
 * withdrawn / handed off / refused before that start exists.
 */
export const createDeferredTurnOperation = (turnId: string): DeferredTurnOperation => {
  let settled = false;
  const { promise: accepted, deferred: acceptedDeferred } = createAcceptedPromise();
  const { promise: terminal, deferred: terminalDeferred } = createTerminalPromise();

  return {
    operation: { turnId, accepted, terminal },
    adopt: (real) => {
      if (settled) return;
      settled = true;
      void real.accepted.then(acceptedDeferred.resolve, acceptedDeferred.reject);
      void real.terminal.then(terminalDeferred.resolve);
    },
    abandon: (reason, error) => {
      if (settled) return;
      settled = true;
      acceptedDeferred.reject(error ?? new TurnOperationAbandonedError(reason));
      terminalDeferred.resolve({ kind: 'abandoned', turnId, reason });
    },
  };
};

export type TurnOperationRegistry = {
  begin: (clientTurnId: string) => TurnOperation;
  accept: (clientTurnId: string, accepted: AcceptedTurn) => void;
  reject: (clientTurnId: string, error: unknown) => void;
  settleNamed: (turnId: string, kind: NamedTurnTerminalKind) => void;
  settleUnnamed: () => void;
  abandonOutstanding: (
    reason: Extract<TurnAbandonReason, 'run_changed' | 'unmounted'>
  ) => void;
  size: () => number;
};

const namedOutcome = (
  kind: NamedTurnTerminalKind,
  turnId: string
): TurnTerminalOutcome => {
  switch (kind) {
    case 'completed':
      return { kind: 'completed', turnId };
    case 'cancelled':
      return { kind: 'cancelled', turnId };
    case 'failed':
      return { kind: 'failed', turnId };
  }
};

/**
 * Exact identity and completion for one local turn. Phase/cursor stay on
 * `turnLifecycleReducer`; this registry only answers "which operation ended".
 */
export const createTurnOperationRegistry = (): TurnOperationRegistry => {
  const byClientId = new Map<string, OperationRecord>();
  const byAcceptedId = new Map<string, OperationRecord>();
  const records = new Set<OperationRecord>();
  const rememberedNamed = new Map<string, NamedTurnTerminalKind>();

  const acceptedUnsettled = (): OperationRecord[] =>
    [...records].filter((record) => record.acceptedSettled && !record.terminalSettled);

  const rememberNamed = (turnId: string, kind: NamedTurnTerminalKind): void => {
    rememberedNamed.delete(turnId);
    rememberedNamed.set(turnId, kind);
    while (rememberedNamed.size > RECENT_TERMINAL_MEMORY) {
      const oldest = rememberedNamed.keys().next().value;
      if (oldest === undefined) break;
      rememberedNamed.delete(oldest);
    }
  };

  const findRemembered = (
    ids: readonly string[]
  ): RememberedNamedTerminal | undefined => {
    let found: RememberedNamedTerminal | undefined;
    for (const [turnId, kind] of rememberedNamed) {
      if (ids.includes(turnId)) found = { turnId, kind };
    }
    return found;
  };

  const forgetRemembered = (ids: readonly string[]): void => {
    for (const id of ids) rememberedNamed.delete(id);
  };

  const remove = (record: OperationRecord): void => {
    records.delete(record);
    byClientId.delete(record.clientTurnId);
    if (record.acceptedTurnId !== null) {
      byAcceptedId.delete(record.acceptedTurnId);
    }
  };

  const maybeGc = (record: OperationRecord): void => {
    if (record.acceptedSettled && record.terminalSettled) remove(record);
  };

  const settleTerminal = (
    record: OperationRecord,
    outcome: TurnTerminalOutcome
  ): void => {
    if (record.terminalSettled) return;
    record.terminalSettled = true;
    record.terminal.resolve(outcome);
    maybeGc(record);
  };

  const resolveAccepted = (record: OperationRecord, accepted: AcceptedTurn): void => {
    if (record.acceptedSettled) return;
    record.acceptedSettled = true;
    record.accepted.resolve(accepted);
    maybeGc(record);
  };

  const rejectAccepted = (record: OperationRecord, error: unknown): void => {
    if (record.acceptedSettled) return;
    record.acceptedSettled = true;
    record.accepted.reject(error);
    maybeGc(record);
  };

  const lookup = (turnId: string): OperationRecord | undefined =>
    byClientId.get(turnId) ?? byAcceptedId.get(turnId);

  const begin = (clientTurnId: string): TurnOperation => {
    const { promise: accepted, deferred: acceptedDeferred } = createAcceptedPromise();
    const { promise: terminal, deferred: terminalDeferred } = createTerminalPromise();
    const record: OperationRecord = {
      clientTurnId,
      acceptedTurnId: null,
      accepted: acceptedDeferred,
      terminal: terminalDeferred,
      acceptedSettled: false,
      terminalSettled: false,
    };
    byClientId.set(clientTurnId, record);
    records.add(record);

    const earlyClientTerminal = findRemembered([clientTurnId]);
    if (earlyClientTerminal) {
      forgetRemembered([clientTurnId]);
      settleTerminal(
        record,
        namedOutcome(earlyClientTerminal.kind, earlyClientTerminal.turnId)
      );
    }

    return { turnId: clientTurnId, accepted, terminal };
  };

  const accept = (clientTurnId: string, accepted: AcceptedTurn): void => {
    const record = byClientId.get(clientTurnId);
    if (!record) return;

    const backendId = accepted.turnId;
    record.acceptedTurnId = backendId;
    if (backendId !== clientTurnId) {
      byAcceptedId.set(backendId, record);
    }

    resolveAccepted(record, accepted);

    if (record.terminalSettled) return;

    const named = findRemembered([clientTurnId, backendId]);
    if (named) {
      forgetRemembered([clientTurnId, backendId]);
      settleTerminal(record, namedOutcome(named.kind, named.turnId));
    }
  };

  const reject = (clientTurnId: string, error: unknown): void => {
    const record = byClientId.get(clientTurnId);
    if (!record) return;
    rejectAccepted(record, error);
    settleTerminal(record, {
      kind: 'abandoned',
      turnId: record.acceptedTurnId ?? clientTurnId,
      reason: 'submit_rejected',
    });
  };

  const settleNamed = (turnId: string, kind: NamedTurnTerminalKind): void => {
    const record = lookup(turnId);
    if (!record) {
      rememberNamed(turnId, kind);
      return;
    }
    settleTerminal(record, namedOutcome(kind, turnId));
  };

  const settleUnnamed = (): void => {
    // Recovery ordering: the oldest accepted, still-open local operation.
    // Not "only when exactly one exists" and not "settle all".
    const [oldest] = acceptedUnsettled();
    if (!oldest) return;
    settleTerminal(oldest, { kind: 'snapshot', turnId: null });
  };

  const abandonOutstanding = (
    reason: Extract<TurnAbandonReason, 'run_changed' | 'unmounted'>
  ): void => {
    for (const record of records) {
      rejectAccepted(record, new TurnOperationAbandonedError(reason));
      settleTerminal(record, {
        kind: 'abandoned',
        turnId: record.acceptedTurnId ?? record.clientTurnId,
        reason,
      });
    }
  };

  return {
    begin,
    accept,
    reject,
    settleNamed,
    settleUnnamed,
    abandonOutstanding,
    size: () => records.size,
  };
};
